import TitleSection from "@/components/common/TitleSection"
import { TempLayout } from "../../layouts"
import { useSapSupplierStore } from "@/stores/sap-supplier/sapSupplierStore"
import { IGetEmployeeByIdParams } from "@/types"
import { useCallback, useEffect, useState, useRef } from "react"
import { useUserStore } from "@/stores/user/userStore"
import { ISapSupplier } from "@/types/sapSupplier.type"
import SupplierCard from "./components/SupplierCard"
import { useNavigate } from "react-router"
import SapApprovalStatusFilter from "@/features/shared/components/SapApprovalStatusFilter"

const SapSuppliers = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const { suppliers, meta, isLoading, fetchSapSuppliers } = useSapSupplierStore()
  const [allSuppliers, setAllSuppliers] = useState<ISapSupplier[]>([])
  const prevDataLengthRef = useRef(0)
  const [params, setParams] = useState<IGetEmployeeByIdParams>({
    page: 1,
    per_page: 4,
    employee_id: "",
  })

  const handleRefetch = useCallback(async () => {
    if (params.employee_id) {
      await fetchSapSuppliers(params)
    }
  }, [fetchSapSuppliers, params])

  const handleReset = () => {
    setAllSuppliers([])
    prevDataLengthRef.current = 0
    setParams(prev => ({ ...prev, page: 1, approval_status: undefined }))
  }

  const handleAddSupplier = () => {
    navigate("/sapspl/create")
  }

  const handleApprovalStatusChange = (approvalStatus?: string) => {
    setAllSuppliers([])
    prevDataLengthRef.current = 0
    setParams(prev => ({
      ...prev,
      page: 1,
      approval_status: approvalStatus || undefined,
    }))
  }

  useEffect(() => {
    if (userData.employee_id) {
      setParams(prev => ({
        ...prev,
        employee_id: userData.employee_id,
      }))
    }
  }, [userData.employee_id])

  useEffect(() => {
    if (params.employee_id) {
      fetchSapSuppliers(params)
    }
  }, [fetchSapSuppliers, params, userData.employee_id])

  useEffect(() => {
    if (params.page === 1) {
      setAllSuppliers(suppliers)
      prevDataLengthRef.current = suppliers.length
    } else if (suppliers.length !== prevDataLengthRef.current) {
      setAllSuppliers(prev => {
        const existingIds = new Set(prev.map(s => s.record_id))
        const newSuppliers = suppliers.filter(s => !existingIds.has(s.record_id))
        return [...prev, ...newSuppliers]
      })
      prevDataLengthRef.current = suppliers.length
    }
  }, [suppliers, params.page])

  const handleLoadMore = () => {
    if (meta && params.page < meta.total_pages) {
      setParams(prev => ({
        ...prev,
        page: prev.page + 1,
      }))
    }
  }

  return (
    <TempLayout title="SAP - Suppliers" navNavigateUrl="/home">
      {/* Header */}
      <TitleSection title="SAP - Suppliers" onAdd={handleAddSupplier} onRefresh={() => void handleRefetch()} />
      <SapApprovalStatusFilter value={params.approval_status} onChange={handleApprovalStatusChange} />

      <SupplierCard suppliers={allSuppliers} isLoading={isLoading} handleReset={handleReset} onLoadMore={handleLoadMore} hasMore={meta ? params.page < meta.total_pages : true} />
    </TempLayout>
  )
}

export default SapSuppliers
